/**
 * Cloud Story Repository
 *
 * Implements IStoryRepository using Supabase.
 * Maps Supabase rows to domain types.
 * Throws READ_ONLY error when in read-only mode (lapsed subscription).
 */

import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import type { Story, StoryStep, StepType } from '@/types/database'
import type { StoryRecord, StepRecord, StoryWithSteps, RepositoryResult } from '@/types/domain'
import { calculateWordCount } from '@/types/domain'
import type { IStoryRepository, StoryUpdates } from './types'

/**
 * Random emoji covers for new stories
 */
const STORY_EMOJIS = ['📖', '✨', '🌟', '🚀', '🦋', '🌈', '🏰', '🐉', '🦄', '🌸', '🎭', '🎪']

function getRandomEmoji(): string {
  return STORY_EMOJIS[Math.floor(Math.random() * STORY_EMOJIS.length)]
}

/**
 * Converts Supabase Story row to unified StoryRecord
 */
function toStoryRecord(row: Story): StoryRecord {
  return {
    id: row.id,
    childId: row.child_id,
    title: row.title,
    coverEmoji: row.cover_emoji,
    status: row.status,
    wordCount: row.word_count,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

/**
 * Converts Supabase StoryStep row to unified StepRecord
 */
function toStepRecord(row: StoryStep): StepRecord {
  return {
    id: row.id,
    storyId: row.story_id,
    stepType: row.step_type,
    stepOrder: row.step_order,
    content: row.content,
    wordCount: row.word_count,
    promptHint: row.prompt_hint,
    updatedAt: row.updated_at,
  }
}

/**
 * Read-only flag - set by context when subscription is lapsed
 */
let isReadOnlyMode = false

/**
 * Sets the read-only mode for the repository
 */
export function setReadOnlyMode(readOnly: boolean): void {
  isReadOnlyMode = readOnly
}

/**
 * Helper to check if writes are allowed
 */
function checkWriteAccess(): RepositoryResult<never> | null {
  if (isReadOnlyMode) {
    return {
      success: false,
      error: 'Your subscription has lapsed. Please renew to create or edit stories.',
      code: 'READ_ONLY',
    }
  }
  if (!isSupabaseConfigured()) {
    return {
      success: false,
      error: 'Database not configured',
      code: 'NETWORK_ERROR',
    }
  }
  return null
}

/**
 * Updates the total word count for a story (internal helper)
 */
async function updateStoryWordCount(storyId: string): Promise<void> {
  if (!isSupabaseConfigured()) return

  const { data: steps } = await supabase
    .from('story_steps')
    .select('word_count')
    .eq('story_id', storyId)
    .is('deleted_at', null)

  const totalWords = (steps || []).reduce((sum, step) => sum + (step.word_count || 0), 0)

  await supabase
    .from('stories')
    .update({
      word_count: totalWords,
      updated_at: new Date().toISOString(),
    } as Record<string, unknown>)
    .eq('id', storyId)
}

/**
 * Cloud Story Repository implementation
 */
export const cloudStoryRepository: IStoryRepository = {
  async listStories(childId: string): Promise<RepositoryResult<StoryRecord[]>> {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Database not configured', code: 'NETWORK_ERROR' }
    }

    const { data, error } = await supabase
      .from('stories')
      .select('*')
      .eq('child_id', childId)
      .is('deleted_at', null)
      .order('updated_at', { ascending: false })

    if (error) {
      return { success: false, error: error.message, code: 'UNKNOWN' }
    }

    return { success: true, data: (data as Story[]).map(toStoryRecord) }
  },

  async createStory(childId: string, title: string = 'My New Story'): Promise<RepositoryResult<StoryRecord>> {
    const writeError = checkWriteAccess()
    if (writeError) return writeError

    const { data, error } = await supabase
      .from('stories')
      .insert({
        child_id: childId,
        title,
        cover_emoji: getRandomEmoji(),
      } as Record<string, unknown>)
      .select()
      .single()

    if (error) {
      return { success: false, error: error.message, code: 'UNKNOWN' }
    }

    return { success: true, data: toStoryRecord(data as Story) }
  },

  async canCreateStory(_childId: string): Promise<boolean> {
    // Cloud users have unlimited stories (unless read-only)
    return !isReadOnlyMode && isSupabaseConfigured()
  },

  async getStoryCount(childId: string): Promise<number> {
    if (!isSupabaseConfigured()) return 0

    const { count, error } = await supabase
      .from('stories')
      .select('*', { count: 'exact', head: true })
      .eq('child_id', childId)
      .is('deleted_at', null)

    if (error) return 0
    return count || 0
  },

  async getStory(storyId: string): Promise<RepositoryResult<StoryRecord | null>> {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Database not configured', code: 'NETWORK_ERROR' }
    }

    const { data, error } = await supabase
      .from('stories')
      .select('*')
      .eq('id', storyId)
      .is('deleted_at', null)
      .single()

    if (error) {
      if (error.code === 'PGRST116') {
        return { success: true, data: null }
      }
      return { success: false, error: error.message, code: 'UNKNOWN' }
    }

    return { success: true, data: toStoryRecord(data as Story) }
  },

  async getStoryWithSteps(storyId: string): Promise<RepositoryResult<StoryWithSteps | null>> {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Database not configured', code: 'NETWORK_ERROR' }
    }

    const [storyResult, stepsResult] = await Promise.all([
      supabase
        .from('stories')
        .select('*')
        .eq('id', storyId)
        .is('deleted_at', null)
        .single(),
      supabase
        .from('story_steps')
        .select('*')
        .eq('story_id', storyId)
        .is('deleted_at', null)
        .order('step_order', { ascending: true }),
    ])

    if (storyResult.error) {
      if (storyResult.error.code === 'PGRST116') {
        return { success: true, data: null }
      }
      return { success: false, error: storyResult.error.message, code: 'UNKNOWN' }
    }

    if (stepsResult.error) {
      return { success: false, error: stepsResult.error.message, code: 'UNKNOWN' }
    }

    return {
      success: true,
      data: {
        story: toStoryRecord(storyResult.data as Story),
        steps: (stepsResult.data as StoryStep[]).map(toStepRecord),
      },
    }
  },

  async updateStory(storyId: string, updates: StoryUpdates): Promise<RepositoryResult<StoryRecord>> {
    const writeError = checkWriteAccess()
    if (writeError) return writeError

    const dbUpdates: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    }
    if (updates.title !== undefined) dbUpdates.title = updates.title
    if (updates.status !== undefined) dbUpdates.status = updates.status
    if (updates.coverEmoji !== undefined) dbUpdates.cover_emoji = updates.coverEmoji

    const { data, error } = await supabase
      .from('stories')
      .update(dbUpdates)
      .eq('id', storyId)
      .select()
      .single()

    if (error) {
      return { success: false, error: error.message, code: 'UNKNOWN' }
    }

    return { success: true, data: toStoryRecord(data as Story) }
  },

  async updateStep(storyId: string, stepType: StepType, content: string): Promise<RepositoryResult<StepRecord>> {
    const writeError = checkWriteAccess()
    if (writeError) return writeError

    const wordCount = calculateWordCount(content)

    const { data, error } = await supabase
      .from('story_steps')
      .update({
        content,
        word_count: wordCount,
        updated_at: new Date().toISOString(),
        client_rev: Date.now(),
      } as Record<string, unknown>)
      .eq('story_id', storyId)
      .eq('step_type', stepType)
      .select()
      .single()

    if (error) {
      return { success: false, error: error.message, code: 'UNKNOWN' }
    }

    // Update parent story's total word count
    await updateStoryWordCount(storyId)

    return { success: true, data: toStepRecord(data as StoryStep) }
  },

  async deleteStory(storyId: string): Promise<RepositoryResult<void>> {
    const writeError = checkWriteAccess()
    if (writeError) return writeError

    const { error } = await supabase
      .from('stories')
      .update({ deleted_at: new Date().toISOString() } as Record<string, unknown>)
      .eq('id', storyId)

    if (error) {
      return { success: false, error: error.message, code: 'UNKNOWN' }
    }

    return { success: true, data: undefined }
  },
}
